import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { OrgSettings } from "../API";
import { actionErrorMessage } from "./actionErrorMessage";
import BillingPage from "./BillingPage";
import { apiOrgActions, type OrgActions } from "./OrgActions";
import UsersPage from "./UsersPage";

type Tab = "users" | "billing";

/**
 * The Settings page container: loads the org, runs mutations through
 * `actions`, and reloads after each. The tabs (UsersPage, BillingPage) only
 * read props, so they render the same with fake actions and data.
 */
const Settings: React.FC<{ actions?: OrgActions }> = ({ actions = apiOrgActions }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tab: Tab = searchParams.get("tab") === "billing" ? "billing" : "users";

  const [data, setData] = useState<OrgSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      setData(await actions.load());
    } catch {
      setError("Could not load settings.");
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Settings sections (Users, Billing) are admin-only and only linked from the
  // sidebar for admins — send a non-admin who lands here back home.
  useEffect(() => {
    if (data && !data.is_org_admin) navigate("/home", { replace: true });
  }, [data, navigate]);

  // Run a mutation, then refresh from the server (server is the source of truth).
  const run = async (fn: () => Promise<unknown>, fallback = "Something went wrong.") => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e: unknown) {
      // A refused action (409) arrives as the response body with its reason;
      // a bare gateway 500 does not, and gets the action's fallback instead.
      setError(actionErrorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  if (!data || !data.is_org_admin) {
    return <div>{error || "Loading…"}</div>;
  }

  return (
    <>
      {error && <div className="dg-banner">{error}</div>}
      {tab === "users" && <UsersPage data={data} busy={busy} run={run} actions={actions} />}
      {tab === "billing" && <BillingPage data={data} busy={busy} run={run} actions={actions} />}
    </>
  );
};

export default Settings;
